import type { Company, Contact, IdentityMatchCandidate, LeadContact, LeadRelation, PartyChannel } from "../../lib/types";

export type CompanyRow = {
  id: string; owner_id: string; legal_name: string; name?: string | null; trade_name?: string | null;
  cnpj?: string | null; normalized_cnpj?: string | null; site?: string | null;
  segment?: string | null; city?: string | null; state?: string | null;
  status?: Company["status"] | null; merged_into_id?: string | null;
  archived_at?: string | null; created_at: string; updated_at?: string | null;
};

export type ContactRow = {
  id: string; owner_id: string; company_id: string; name: string; role?: string | null;
  photo_url?: string | null; merged_into_id?: string | null;
  created_at: string; updated_at?: string | null;
};

export type PartyChannelRow = {
  id: string; owner_id: string; company_id: string; contact_id?: string | null;
  channel_type: PartyChannel["type"]; value: string; normalized_value: string;
  status?: PartyChannel["status"] | null; is_primary?: boolean | null;
  verified?: boolean | null; created_at: string; updated_at?: string | null;
};

export type LeadContactRow = {
  owner_id: string; lead_id: string; company_id: string; contact_id: string; is_primary?: boolean | null; created_at: string;
};

export type LeadRelationRow = {
  id: string; owner_id: string; source_lead_id: string; target_lead_id: string;
  relation_type: LeadRelation["relationType"]; source_funnel_id?: string | null;
  target_funnel_id?: string | null; idempotency_key?: string | null; command_id?: string | null;
  created_at: string;
};

export type IdentityCandidateRow = {
  id: string; owner_id: string; company_id?: string | null; contact_id?: string | null;
  candidate_company_id?: string | null; candidate_contact_id?: string | null;
  reason: string; confidence?: number | string | null;
  status: IdentityMatchCandidate["status"]; created_at: string; reviewed_at?: string | null;
  reviewed_by?: string | null; review_reason?: string | null;
};

export const mapCompanyRow = (row: CompanyRow): Company => ({
  id: row.id,
  legalName: row.legal_name ?? row.name ?? "",
  tradeName: row.trade_name || undefined,
  cnpj: row.cnpj || undefined,
  normalizedCnpj: row.normalized_cnpj || undefined,
  site: row.site || undefined,
  segment: row.segment || undefined,
  city: row.city || undefined,
  state: row.state || undefined,
  status: row.status ?? (row.merged_into_id ? "merged" : row.archived_at ? "archived" : "active"),
  mergedIntoId: row.merged_into_id || undefined,
  createdAt: row.created_at,
  updatedAt: row.updated_at ?? row.created_at,
});

export const mapContactRow = (row: ContactRow): Contact => ({
  id: row.id,
  companyId: row.company_id,
  name: row.name,
  role: row.role || undefined,
  photoUrl: row.photo_url || undefined,
  mergedIntoId: row.merged_into_id || undefined,
  createdAt: row.created_at,
  updatedAt: row.updated_at ?? row.created_at,
});

export const mapPartyChannelRow = (row: PartyChannelRow): PartyChannel => ({
  id: row.id,
  companyId: row.company_id,
  contactId: row.contact_id || undefined,
  type: row.channel_type,
  value: row.value,
  normalizedValue: row.normalized_value,
  status: row.status ?? "active",
  primary: Boolean(row.is_primary),
  verified: row.verified == null ? undefined : Boolean(row.verified),
  createdAt: row.created_at,
  updatedAt: row.updated_at ?? row.created_at,
});

export const mapLeadContactRow = (row: LeadContactRow): LeadContact => ({
  leadId: row.lead_id,
  contactId: row.contact_id,
  primary: Boolean(row.is_primary),
  createdAt: row.created_at,
});

export const mapLeadRelationRow = (row: LeadRelationRow): LeadRelation => ({
  id: row.id,
  sourceLeadId: row.source_lead_id,
  targetLeadId: row.target_lead_id,
  relationType: row.relation_type,
  sourceFunnelId: row.source_funnel_id || undefined,
  targetFunnelId: row.target_funnel_id || undefined,
  idempotencyKey: row.idempotency_key || undefined,
  commandId: row.command_id || undefined,
  createdAt: row.created_at,
});

export const mapIdentityCandidateRow = (row: IdentityCandidateRow): IdentityMatchCandidate => ({
  id: row.id,
  companyId: row.company_id || undefined,
  contactId: row.contact_id || undefined,
  candidateCompanyId: row.candidate_company_id || undefined,
  candidateContactId: row.candidate_contact_id || undefined,
  reason: row.reason,
  confidence: row.confidence == null ? undefined : Number(row.confidence),
  status: row.status,
  createdAt: row.created_at,
  reviewedAt: row.reviewed_at || undefined,
  reviewedBy: row.reviewed_by || undefined,
  reviewReason: row.review_reason || undefined,
});
